import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Steps,
  Button,
  Space,
  Typography,
  Card,
  Row,
  Col,
  message,
  Radio,
  Form,
  Input,
  Upload,
  Progress,
  Switch,
  Select,
  Tooltip,
  Popover,
  Spin,
  Popconfirm,
  Badge,
  Alert,
  Table,
} from "antd";
import {
  UploadOutlined,
  UsergroupAddOutlined,
  DeleteOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  CheckCircleOutlined,
  SettingOutlined,
  CloseOutlined,
  ArrowRightOutlined,
  SaveOutlined,
  MinusOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import Papa from "papaparse";
import PaginationTable from "../../../shared/PaginationTable";
import TemplateCSVDownloader from "../../../shared/TemplateCSVDownloader";
import SourceAudienceSelectionModal from "./SourceAudienceSelectionModal";
import { HashedEmailConfig } from "../../OrgConfig/configs/emailConfig";
import { PhoneNumberConfig } from "../../OrgConfig/configs/phoneNumberConfig";
import { LookalikeConfig } from "../../OrgConfig/configs/lookalikeConfig";
import {
  encryptStringSHA512,
  encryptStringSHA256,
  encryptStringSHA3,
  encryptStringSHA1,
  encryptStringMD5,
} from "../../../../core/utils/encryptString";
import { useQuery, useMutation } from "react-apollo";
import GET_FILTERED_HASHED_EMAIL_LIST_WITH_COUNT from "../../../../core/GraphQl/Queries/GET_FILTERED_HASHED_EMAIL_LIST_WITH_COUNT";
import PROCESS_MODEL_SCORE from "../../../../core/GraphQl/Mutations/PROCESS_MODEL_SCORE";
import { useUploadProgress } from "../../../../core/components/firstPartyData/providers/UploadProgressProvider";

const { Step } = Steps;
const { Title, Text } = Typography;
const { Dragger } = Upload;

// Helper function to map DSP names based on providersVisible permission
const getDSPDisplayName = (dspName, providersVisible) => {
  if (providersVisible) {
    return dspName; // Show actual DSP name
  }
  
  // Map to tier names when providersVisible is false
  switch (dspName?.toLowerCase()) {
    case 'liquidm':
      return 'Tier 1';
    case 'nexxen':
      return 'Tier 2';
    default:
      return dspName; // Return original if not mapped
  }
};

const GuidedWorkflowSteps = ({
  visible,
  onClose,
  createHashedEmailList,
  bulkCreateHashedEmailAddress,
  activateHashedEmailList,
  refetchHashedEmailLists,
  updateHashedEmailList,
  currentOrg,
  providersVisible,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();

  const {
    startUpload,
    updateUploadProgress,
    completeUpload,
    failUpload,
    minimizeUpload,
    uploadState,
    isMinimized,
    clearUpload,
  } = useUploadProgress();
  
  // Step 1 - Upload Data states
  const [emails, setEmails] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadComplete, setUploadComplete] = useState(false);
  const [progress, setProgress] = useState(0);
  const [savedHashedEmailCount, setHashedEmailCount] = useState(0);
  const [emailsAlreadyHashed, setEmailsAlreadyHashed] = useState(false);
  const [hemAutoDetected, setHemAutoDetected] = useState(false);
  const [selectedHashingMethod, setSelectedHashingMethod] = useState("SHA256");
  const [detectedInvalidFormat, setDetectedInvalidFormat] = useState(null); // Track invalid data format
  const [invalidRecords, setInvalidRecords] = useState([]); // { row: number, value: string }
  const [sortOrder, setSortOrder] = useState("ascending");
  const [createdEmailListId, setCreatedEmailListId] = useState(null);
  const [selectedDataType, setSelectedDataType] = useState("emails");

  // Lookalike audience states
  const [selectedLookalikeSource, setSelectedLookalikeSource] = useState("existing");
  const [selectedSourceAudience, setSelectedSourceAudience] = useState(null);
  const [showSourceAudienceModal, setShowSourceAudienceModal] = useState(false);
  const [selectedCsvDataType, setSelectedCsvDataType] = useState("emails"); // For CSV upload path
  const [hasActivatedAudiences, setHasActivatedAudiences] = useState(false); // Track if activated audiences exist - default false until verified

  // Per-data-type name storage to prevent state leakage between data types
  const [emailsAudienceName, setEmailsAudienceName] = useState("");
  const [phonesAudienceName, setPhonesAudienceName] = useState("");
  const [lookalikeAudienceName, setLookalikeAudienceName] = useState("");

  // Track previous data type to detect changes
  const prevDataTypeRef = useRef(selectedDataType);

  // Upload error message state for tooltip
  const [uploadErrorMessage, setUploadErrorMessage] = useState(null);

  // CSV parsing state
  const [isParsing, setIsParsing] = useState(false);
  const [parsingProgress, setParsingProgress] = useState(0);

  // Step 2 - Map to Device IDs states
  const [isMappingDevices, setIsMappingDevices] = useState(false);
  const [isMappingComplete, setIsMappingComplete] = useState(false);
  
  // DSP provider selection for mapping step
  const [selectedDspProvider, setSelectedDspProvider] = useState("liquidm");

  // Query to check for activated audiences when lookalike is selected
  const {
    data: audienceCheckData,
    loading: loadingAudienceCheck,
  } = useQuery(GET_FILTERED_HASHED_EMAIL_LIST_WITH_COUNT, {
    variables: {
      orgId: currentOrg?.id,
      searchTerm: null
    },
    fetchPolicy: 'cache-and-network',
    skip: selectedDataType !== "lookalike" || !currentOrg?.id,
  });

  // Mutation for processing Model Score
  const [processModelScore] = useMutation(PROCESS_MODEL_SCORE);

  // Update hasActivatedAudiences based on query result
  useEffect(() => {
    if (selectedDataType === "lookalike") {
      if (!loadingAudienceCheck && audienceCheckData) {
        const hashedEmailList = audienceCheckData?.getFilteredHashedEmailListWithCount?.hashedEmailList;

        if (Array.isArray(hashedEmailList)) {
          // Filter for ACTIVATED status OR legacy activated field
          const activatedCount = hashedEmailList.filter(aud =>
            aud.status === "ACTIVATED" || aud.activated === true
          ).length;

          setHasActivatedAudiences(activatedCount > 0);
        } else {
          setHasActivatedAudiences(false);
        }
      }
      // While loading, keep the current state (don't show error prematurely)
    } else {
      // Reset to true when not on lookalike
      setHasActivatedAudiences(true);
    }
  }, [selectedDataType, audienceCheckData, loadingAudienceCheck]);

  // Guard against stale selection - clear if selected audience no longer exists
  useEffect(() => {
    if (selectedSourceAudience && selectedDataType === "lookalike" && !loadingAudienceCheck && audienceCheckData) {
      const hashedEmailList = audienceCheckData?.getFilteredHashedEmailListWithCount?.hashedEmailList;

      if (Array.isArray(hashedEmailList)) {
        const audienceStillExists = hashedEmailList.some(aud =>
          aud.id === selectedSourceAudience.id &&
          (aud.status === "ACTIVATED" || aud.activated === true) &&
          aud.csvFileName
        );

        if (!audienceStillExists) {
          setSelectedSourceAudience(null);
          message.warning("The selected audience is no longer available. Please select a different audience.");
        }
      }
    }
  }, [selectedSourceAudience, selectedDataType, audienceCheckData, loadingAudienceCheck]);

  // Auto-switch to Nexxen if phones or lookalike are selected since LiquidM doesn't support them
  useEffect(() => {
    if ((selectedDataType === "phones" || selectedDataType === "lookalike") && selectedDspProvider === "liquidm") {
      setSelectedDspProvider("nexxen");
    }
  }, [selectedDataType, selectedDspProvider]);

  // Auto-enable hashed toggle when lookalike is selected (lookalike data must be hashed)
  useEffect(() => {
    if (selectedDataType === "lookalike" && !emailsAlreadyHashed) {
      setEmailsAlreadyHashed(true);
    }
  }, [selectedDataType, emailsAlreadyHashed]);

  // Handle data type switching: save current name and restore saved name for new type
  useEffect(() => {
    const prevDataType = prevDataTypeRef.current;

    // Only process if data type actually changed
    if (prevDataType !== selectedDataType) {
      // Save current form name to the previous data type's storage
      const currentName = form.getFieldValue("name") || "";
      if (prevDataType === "emails") {
        setEmailsAudienceName(currentName);
      } else if (prevDataType === "phones") {
        setPhonesAudienceName(currentName);
      } else if (prevDataType === "lookalike") {
        setLookalikeAudienceName(currentName);
      }

      // Restore the saved name for the new data type
      let savedName = "";
      if (selectedDataType === "emails") {
        savedName = emailsAudienceName;
      } else if (selectedDataType === "phones") {
        savedName = phonesAudienceName;
      } else if (selectedDataType === "lookalike") {
        savedName = lookalikeAudienceName;
      }
      form.setFieldsValue({ name: savedName });

      // Reset lookalike-specific states when switching away from lookalike
      if (selectedDataType !== "lookalike") {
        setSelectedLookalikeSource("existing");
        setSelectedSourceAudience(null);
        setSelectedCsvDataType("emails");

        // Reset "Data is already hashed" toggle when switching away from lookalike
        // UNLESS data was auto-detected as hashed from an uploaded file
        if (emails.length === 0 || !hemAutoDetected) {
          setEmailsAlreadyHashed(false);
        }
      }

      // Update the ref to current data type
      prevDataTypeRef.current = selectedDataType;
    }
  }, [selectedDataType, emailsAudienceName, phonesAudienceName, lookalikeAudienceName, emails.length, hemAutoDetected, form]);

  // Auto-populate audience name when source audience is selected (for lookalike only)
  useEffect(() => {
    if (selectedSourceAudience && selectedDataType === "lookalike") {
      const baseName = selectedSourceAudience.name.trim();
      const generatedName = baseName.endsWith("_Lookalike")
        ? baseName
        : `${baseName}_Lookalike`;
      form.setFieldsValue({ name: generatedName });
      setLookalikeAudienceName(generatedName);
    }
  }, [selectedSourceAudience, selectedDataType, form]);

  // Clear upload error message when data type or source audience changes
  useEffect(() => {
    if (uploadErrorMessage) {
      setUploadErrorMessage(null);
    }
  }, [selectedDataType, selectedSourceAudience, selectedLookalikeSource, uploadErrorMessage]);

  // Restore state when upload completes while modal is closed
  useEffect(() => {
    if (visible && uploadState && uploadState.status === "completed" && !isUploadComplete) {
      form.setFieldsValue({ name: uploadState.audienceName });
      setSelectedDataType(uploadState.dataType || "emails");
      setUploadComplete(true);
      setProgress(100);
      setHashedEmailCount(uploadState.savedRecords || 0);
      setCreatedEmailListId(uploadState.emailListId);
      setCurrentStep(uploadState.currentStep || 0);
      message.success("Upload completed while you were away!");
    }
  }, [visible, uploadState, isUploadComplete, form]);

  const emailRegex = /\S+@\S+\.\S+/;
  const phoneRegex = /^(\+?\d{1,3}[- ]?)?(\(?\d{1,4}\)?[- ]?)?[\d\- ]{7,15}$/;

  const getDataTypeLabel = () => {
    switch (selectedDataType) {
      case "emails": return "Emails";
      case "phones": return "Phone Numbers";
      case "lookalike": return "Lookalike Audiences";
      default: return "Data";
    }
  };

  const steps = [
    {
      title: `Upload ${getDataTypeLabel()}`,
      icon: <UploadOutlined />,
      description: `Your ${getDataTypeLabel().toLowerCase()} has been successfully uploaded and is ready for processing`,
    },
    {
      title: `Convert ${getDataTypeLabel()} to Device IDs`,
      icon: <UsergroupAddOutlined />,
      description: `Converting ${getDataTypeLabel().toLowerCase()} into device identifiers through secure hashing and identity matching`,
    },
  ];

  // Function to clean HEM markers from data
  const cleanHEMMarkers = (dataArray) => {
    // Filter out common HEM column headers and markers
    const hemMarkers = [
      'HEM',
      'HEM (Invalid Value)',
      'Hashed Email',
      'Hashed Emails',
      'Email Hash',
      'SHA256',
      'Hash'
    ];

    return dataArray.filter(item => {
      const trimmedItem = item.trim();
      // Filter out exact matches (case insensitive) and empty values
      return trimmedItem && !hemMarkers.some(marker =>
        marker.toLowerCase() === trimmedItem.toLowerCase()
      );
    });
  };

  // Function to detect if data is already hashed and determine the hash type
  // Also detects invalid/unsupported data formats
  const detectHashedEmails = (dataArray) => {
    if (dataArray.length === 0) return { isHashed: false, detectedMethod: null, isInvalid: false, invalidFormat: null };

    const hexRegex = /^[0-9a-fA-F]+$/;
    const base64Regex = /^[A-Za-z0-9+/=]+$/;
    const base64UrlRegex = /^[A-Za-z0-9_-]+$/;

    let hashCounts = { MD5: 0, SHA1: 0, SHA256: 0, SHA512: 0 };
    let originalFormatCount = 0;
    let invalidCounts = { base64: 0, base64url: 0, other: 0 };

    dataArray.forEach((item) => {
      const trimmedItem = item.trim();
      if (trimmedItem) {
        const len = trimmedItem.length;
        const isHex = hexRegex.test(trimmedItem);

        // Check for valid hash formats
        if (isHex) {
          if (len === 32) hashCounts.MD5++;
          else if (len === 40) hashCounts.SHA1++;
          else if (len === 64) hashCounts.SHA256++;
          else if (len === 128) hashCounts.SHA512++;
          else invalidCounts.other++;
        }
        // Check for original format (email/phone)
        else {
          const isOriginalFormat = selectedDataType === "emails" ? emailRegex.test(trimmedItem) : phoneRegex.test(trimmedItem);
          if (isOriginalFormat) {
            originalFormatCount++;
          }
          // Check for Base64 URL-safe format (commonly used for hashed data)
          else if (base64UrlRegex.test(trimmedItem) && len >= 20 && len <= 50) {
            invalidCounts.base64url++;
          }
          // Check for standard Base64
          else if (base64Regex.test(trimmedItem) && len >= 20) {
            invalidCounts.base64++;
          }
          else {
            invalidCounts.other++;
          }
        }
      }
    });

    // Find the most common hash type
    const totalHashes = hashCounts.MD5 + hashCounts.SHA1 + hashCounts.SHA256 + hashCounts.SHA512;
    const totalInvalid = invalidCounts.base64 + invalidCounts.base64url + invalidCounts.other;
    const totalEntries = dataArray.length;

    // Check if most data is valid hashes
    if (totalHashes > 0) {
      const hashPercentage = totalHashes / totalEntries;
      if (hashPercentage > 0.7) {
        // Determine which hash method was detected
        const sorted = Object.entries(hashCounts).sort((a, b) => b[1] - a[1]);
        const detectedMethod = sorted[0][1] > 0 ? sorted[0][0] : "SHA256";
        return { isHashed: true, detectedMethod, isInvalid: false, invalidFormat: null };
      }
    }

    // Check if most data is invalid/unsupported format
    if (totalInvalid > 0) {
      const invalidPercentage = totalInvalid / totalEntries;
      if (invalidPercentage > 0.5) {
        // Determine which invalid format was detected
        let invalidFormat = null;
        if (invalidCounts.base64url > invalidCounts.base64 && invalidCounts.base64url > invalidCounts.other) {
          invalidFormat = "Base64 URL-safe encoded";
        } else if (invalidCounts.base64 > invalidCounts.other) {
          invalidFormat = "Base64 encoded";
        } else if (invalidCounts.other > 0) {
          invalidFormat = "unsupported";
        }
        return { isHashed: false, detectedMethod: null, isInvalid: true, invalidFormat };
      }
    }

    return { isHashed: false, detectedMethod: null, isInvalid: false, invalidFormat: null };
  };

  const sortIcon = sortOrder === "ascending" ? <SortAscendingOutlined /> : <SortDescendingOutlined />;

  // Email validation and processing functions
  const removeDuplicates = arr => Array.from(new Set(arr));

  const convertToLowercase = arr => arr.map(e => e.toLowerCase());

  const containsEmail = arr => arr.some(element => emailRegex.test(element));

  const findInvalidEmail = arr => arr.find(element => !emailRegex.test(element)) || null;

  const containsPhoneNumber = arr => arr.some(element => phoneRegex.test(element));

  const findInvalidPhoneNumber = arr => arr.find(element => !phoneRegex.test(element)) || null;

  // Check if array contains non-hashed values (accepts MD5, SHA1, SHA256, SHA512)
  function hasNonHashedValue(arr) {
    const hexRegex = /^[0-9a-fA-F]+$/;
    return !arr.every(str => {
      const isValidLength = str.length === 32 || str.length === 40 || str.length === 64 || str.length === 128;
      return (
        isValidLength &&
        hexRegex.test(str) &&
        str !== str.split("").reverse().join("")
      );
    });
  }

  // Validate if a single string is a valid hash format (accepts MD5, SHA1, SHA256, SHA512)
  function isValidHashedValue(str) {
    const hexRegex = /^[0-9a-fA-F]+$/;
    const isValidLength = str.length === 32 || str.length === 40 || str.length === 64 || str.length === 128;
    const isHexFormat = hexRegex.test(str);
    const isNotPalindrome = str !== str.split("").reverse().join("");
    return isValidLength && isHexFormat && isNotPalindrome;
  }

  // Download invalid records as CSV
  const downloadInvalidRecordsCsv = () => {
    const csvContent = Papa.unparse({
      fields: ["Row", "Value"],
      data: invalidRecords.map(r => [r.row, r.value])
    });
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "invalid_records.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Get expected hash length for a given hash method
  const getHashLength = (method) => {
    switch (method) {
      case "MD5": return 32;
      case "SHA1": return 40;
      case "SHA256": return 64;
      case "SHA512": return 128;
      default: return 64;
    }
  };

  // Function to detect the format of uploaded data
  const detectDataFormat = (dataArray) => {
    if (!dataArray || dataArray.length === 0) return null;

    const hexRegex = /^[0-9a-fA-F]+$/;
    const base64Regex = /^[A-Za-z0-9+/=]+$/;
    const base64UrlRegex = /^[A-Za-z0-9_-]+$/;

    let formatCounts = {
      md5: 0,
      sha1: 0,
      sha256: 0,
      sha512: 0,
      base64: 0,
      base64url: 0,
      unknown: 0
    };

    const sampleSize = Math.min(dataArray.length, 100);
    const sample = dataArray.slice(0, sampleSize);

    sample.forEach((item) => {
      const trimmedItem = item.trim();
      if (!trimmedItem) return;

      const len = trimmedItem.length;
      const isHex = hexRegex.test(trimmedItem);

      // Check for hex-based hashes first
      if (isHex) {
        if (len === 32) formatCounts.md5++;
        else if (len === 40) formatCounts.sha1++;
        else if (len === 64) formatCounts.sha256++;
        else if (len === 128) formatCounts.sha512++;
        else formatCounts.unknown++;
      }
      // Check for Base64 URL-safe format (commonly used for hashed data)
      else if (base64UrlRegex.test(trimmedItem) && len >= 20 && len <= 50) {
        formatCounts.base64url++;
      }
      // Check for standard Base64
      else if (base64Regex.test(trimmedItem) && len >= 20) {
        formatCounts.base64++;
      }
      else {
        formatCounts.unknown++;
      }
    });

    // Find the most common format
    const total = sampleSize;
    const entries = Object.entries(formatCounts).filter(([key]) => key !== 'unknown');
    const sorted = entries.sort((a, b) => b[1] - a[1]);

    if (sorted.length > 0 && sorted[0][1] > 0) {
      const [format, count] = sorted[0];
      const percentage = (count / total) * 100;

      if (percentage >= 50) {
        const formatNames = {
          md5: 'MD5 hash',
          sha1: 'SHA-1 hash',
          sha256: 'SHA-256 hash',
          sha512: 'SHA-512 hash',
          base64: 'Base64 encoded',
          base64url: 'Base64 URL-safe encoded'
        };
        return formatNames[format] || format;
      }
    }

    return null;
  };

  // Function to validate that all data matches the selected hash format
  const validateHashFormat = (dataArray, expectedMethod) => {
    if (!dataArray || dataArray.length === 0) return { isValid: true, detectedFormat: null };

    const hexRegex = /^[0-9a-fA-F]+$/;
    const expectedLength = getHashLength(expectedMethod);
    let invalidCount = 0;
    let detectedFormat = null;

    // Check a sample for the detected format
    const sampleSize = Math.min(dataArray.length, 100);
    const sample = dataArray.slice(0, sampleSize);

    let formatCounts = { MD5: 0, SHA1: 0, SHA256: 0, SHA512: 0, other: 0 };

    sample.forEach((item) => {
      const trimmedItem = item.trim();
      if (!trimmedItem) return;

      const len = trimmedItem.length;
      const isHex = hexRegex.test(trimmedItem);

      // Check if it matches the expected format
      if (len === expectedLength && isHex) {
        return; // Valid for the expected method
      }

      invalidCount++;

      // Track what format was detected instead
      if (isHex) {
        if (len === 32) formatCounts.MD5++;
        else if (len === 40) formatCounts.SHA1++;
        else if (len === 64) formatCounts.SHA256++;
        else if (len === 128) formatCounts.SHA512++;
        else formatCounts.other++;
      } else {
        formatCounts.other++;
      }
    });

    if (invalidCount > 0) {
      // Determine the most common detected format
      const sorted = Object.entries(formatCounts).sort((a, b) => b[1] - a[1]);
      if (sorted[0][1] > 0 && sorted[0][0] !== 'other') {
        const formatNames = {
          MD5: 'MD5 (32 characters)',
          SHA1: 'SHA-1 (40 characters)',
          SHA256: 'SHA-256 (64 characters)',
          SHA512: 'SHA-512 (128 characters)',
        };
        detectedFormat = formatNames[sorted[0][0]];
      }
      return { isValid: false, detectedFormat };
    }

    return { isValid: true, detectedFormat: null };
  };

  const encryptStringBasedOnHashSelection = value => {
    switch (selectedHashingMethod) {
      case "SHA512":
        return encryptStringSHA512(value);
      case "SHA256":
        return encryptStringSHA256(value);
      case "SHA3":
        return encryptStringSHA3(value);
      case "SHA1":
        return encryptStringSHA1(value);
      case "MD5":
        return encryptStringMD5(value);
      default:
        return encryptStringSHA256(value);
    }
  };

  // Progress tracking function
  const updateProgress = async (stepProgress, status, dsp = null) => {
    if (!createdEmailListId) return;
    
    try {
      await updateHashedEmailList({
        id: createdEmailListId,
        stepProgress: stepProgress,
        status: status,
        ...(dsp && { dsp: dsp.toUpperCase() })
      });
    } catch (error) {
      console.error("Error updating progress:", error);
    }
  };

  const customRequest = ({ file, onError }) => {
    if (file.name.toLowerCase().includes("csv")) {
      // Reset upload complete state when starting a new CSV upload
      if (isUploadComplete) {
        setUploadComplete(false);
        setHashedEmailCount(0);
        setProgress(0);
      }

      // Set parsing state to show UI feedback
      setIsParsing(true);
      setParsingProgress(0);

      // Variables to track parsing progress
      const allRows = [];
      const fileSize = file.size;
      let lastProgress = 0;

      Papa.parse(file, {
        skipEmptyLines: "greedy",
        // Note: Web Worker mode causes issues with data access in some environments
        // Using chunked parsing instead for performance without worker thread
        chunkSize: 1024 * 1024 * 10, // Process in 10MB chunks to prevent browser hang

        // Chunk callback fires for each chunk parsed - use for accurate progress tracking
        chunk: function(results, parser) {
          // Store rows from this chunk - use concat to avoid stack overflow with large arrays
          if (results.data && results.data.length > 0) {
            // Use Array.prototype.push.apply with slice for large arrays to avoid stack overflow
            for (let i = 0; i < results.data.length; i++) {
              allRows.push(results.data[i]);
            }
          }

          // Calculate progress based on file stream position
          // parser.streamer gives us access to the current byte position
          if (parser.streamer && parser.streamer._input) {
            // Calculate how much of the file has been read
            const streamedLength = parser.streamer._input.length;
            const progress = Math.min(90, Math.floor((streamedLength / fileSize) * 100));

            // Only update if progress has increased by at least 1%
            if (progress > lastProgress) {
              lastProgress = progress;
              setParsingProgress(progress);
            }
          }
        },

        error: function (err, file, inputElem, reason) {
          setIsParsing(false);
          setParsingProgress(0);
          message.error("Error parsing CSV file");
          onError({ err, file, inputElem, reason });
        },

        complete: async () => {
          // Set progress to 90% as we start processing the data
          setParsingProgress(90);

          // Use setTimeout to defer processing and prevent UI blocking
          setTimeout(async () => {
            try {
              setParsingProgress(93);

              // Track original row numbers before flattening
              const valueToRow = new Map();
              const flatValues = [];
              allRows.forEach((row, rowIndex) => {
                if (Array.isArray(row)) {
                  row.forEach(cell => {
                    const val = typeof cell === 'string' ? cell.trim() : String(cell).trim();
                    if (val) {
                      flatValues.push(val);
                      const key = val.toLowerCase();
                      if (!valueToRow.has(key)) {
                        valueToRow.set(key, rowIndex + 1);
                      }
                    }
                  });
                }
              });

              let currentEmails = removeDuplicates(flatValues);

              setParsingProgress(95);
              // Clean HEM markers from the data
              const cleanedEmails = cleanHEMMarkers(currentEmails);
              const wasHEMCleaned = cleanedEmails.length < currentEmails.length;

              // Use cleaned data
              currentEmails = cleanedEmails;

              setParsingProgress(97);
              // Auto-detect if data is hashed before processing
              const hashDetection = detectHashedEmails(currentEmails);
              const areHashedEmails = hashDetection.isHashed;

              // Only convert to lowercase for emails if not hashed
              // For lookalike CSV upload path, use selectedCsvDataType
              let processedEmails = currentEmails;
              const effectiveType =
                selectedDataType === "lookalike" && selectedLookalikeSource === "upload"
                  ? selectedCsvDataType
                  : selectedDataType;

              if (effectiveType === "emails" && !areHashedEmails) {
                processedEmails = convertToLowercase(currentEmails);
              }

              setParsingProgress(99);
              const combinedEmails = [...emails, ...processedEmails];
              const uniqueEmails = removeDuplicates(combinedEmails);

              // Split valid and invalid records
              const validRecords = [];
              const newInvalidRecords = [];

              uniqueEmails.forEach(record => {
                const trimmed = record.trim();
                let isValid = true;

                if (hashDetection.isInvalid) {
                  isValid = false;
                } else if (areHashedEmails || emailsAlreadyHashed) {
                  isValid = isValidHashedValue(trimmed);
                } else if (effectiveType === "emails") {
                  isValid = emailRegex.test(trimmed);
                }
                // For phones (not hashed): all valid per feature scope

                if (isValid) {
                  validRecords.push(record);
                } else {
                  const rowNum = valueToRow.get(record.toLowerCase()) || valueToRow.get(record) || 0;
                  newInvalidRecords.push({ row: rowNum, value: record });
                }
              });

              setEmails(validRecords);

              // Accumulate invalid records (dedup by value)
              if (newInvalidRecords.length > 0) {
                setInvalidRecords(prev => {
                  const combined = [...prev, ...newInvalidRecords];
                  const seen = new Set();
                  return combined.filter(r => {
                    if (seen.has(r.value)) return false;
                    seen.add(r.value);
                    return true;
                  });
                });
              }

              // Set to 100% before showing success
              setParsingProgress(100);

              // Check for invalid/unsupported data format
              if (hashDetection.isInvalid) {
                setDetectedInvalidFormat(hashDetection.invalidFormat || "unsupported");
                const dataLabel = effectiveType === "emails" ? "email addresses" : "phone numbers";
                const formatMsg = hashDetection.invalidFormat
                  ? `Your data appears to be ${hashDetection.invalidFormat}.`
                  : "Your data is in an unsupported format.";
                message.warning({
                  content: `${formatMsg} Only plain ${dataLabel} or hashed data (MD5, SHA-1, SHA-256, SHA-512) are supported.`,
                  duration: 5
                });
              }
              // Auto-detect if data is hashed and toggle the switch
              else if (areHashedEmails && !emailsAlreadyHashed) {
                setDetectedInvalidFormat(null);
                setEmailsAlreadyHashed(true);
                setHemAutoDetected(true);
                // Set the detected hash method
                const detectedMethod = hashDetection.detectedMethod || "SHA256";
                setSelectedHashingMethod(detectedMethod);

                // Show appropriate message based on whether HEM markers were cleaned
                if (wasHEMCleaned) {
                  message.success({ content: "Detected HEM. Cleaned and set hashing to SHA256.", duration: 3 });
                } else {
                  const dataLabel = effectiveType === "emails" ? "emails" : "phone numbers";
                  if (newInvalidRecords.length > 0) {
                    message.warning({
                      content: `Detected ${detectedMethod} hashed ${dataLabel}. ${validRecords.length.toLocaleString()} valid records loaded, ${newInvalidRecords.length.toLocaleString()} invalid records found.`,
                      duration: 5
                    });
                  } else {
                    message.info({ content: `Detected ${detectedMethod} hashed ${dataLabel} - automatically enabled pre-hashed data mode`, duration: 3 });
                  }
                }
              } else {
                setDetectedInvalidFormat(null);
                if (newInvalidRecords.length > 0) {
                  message.warning({
                    content: `Loaded ${validRecords.length.toLocaleString()} valid records. ${newInvalidRecords.length.toLocaleString()} invalid records found.`,
                    duration: 5
                  });
                } else {
                  message.success({ content: `Successfully loaded ${validRecords.length.toLocaleString()} records`, duration: 3 });
                }
              }

              // Small delay before hiding the progress indicator to show 100%
              setTimeout(() => {
                setIsParsing(false);
                setParsingProgress(0);
              }, 500);
            } catch (error) {
              message.error({ content: "Error processing CSV data", duration: 3 });
              setIsParsing(false);
              setParsingProgress(0);
            }
          }, 100);
        },
      });
    } else {
      message.error("Invalid file type. Only accepts .csv");
      return;
    }
  };

  const isNameEmpty = () => {
    const nameValue = form.getFieldValue("name");
    return !nameValue || nameValue.trim() === "";
  };

  const isNameValid = () => {
    const nameValue = form.getFieldValue("name");
    if (!nameValue || nameValue.trim() === "") return false;
    const validNameRegex = /^[a-zA-Z0-9_-]+$/;
    return validNameRegex.test(nameValue.trim());
  };

  const handleSortByName = () => {
    let sorted;
    if (sortOrder === "ascending") {
      sorted = [...emails].sort((a, b) => a.localeCompare(b));
      setSortOrder("descending");
    } else {
      sorted = [...emails].sort((a, b) => b.localeCompare(a));
      setSortOrder("ascending");
    }
    setEmails(sorted);
  };

  // Handle upload to Model Score pipeline for lookalike audiences
  const handleUploadToModelScore = async () => {
    if (!selectedSourceAudience) {
      return message.error("Please select a source audience to continue.");
    }

    if (isNameEmpty()) {
      return message.error("Please input list name.");
    }

    if (!isNameValid()) {
      return message.error("Name can only contain letters, numbers, dash (-) and underscore (_). No spaces or other special characters allowed.");
    }

    setIsUploading(true);

    try {
      // Process Model Score using the processModelScore mutation
      const processResponse = await processModelScore({
        variables: {
          orgID: currentOrg?.id,
          hashedEmailListId: selectedSourceAudience.id,
          audienceName: form.getFieldValue("name"),
        }
      });

      if (processResponse && processResponse.data && processResponse.data.processModelScore) {
        // Parse the response as it may be a JSON string or object
        let parsedResponse;
        const responseData = processResponse.data.processModelScore;

        try {
          parsedResponse = typeof responseData === 'string' ? JSON.parse(responseData) : responseData;
        } catch (e) {
          // If parsing fails, treat the response as a success (legacy behavior)
          parsedResponse = { success: true };
        }

        // Check if the response indicates failure
        if (parsedResponse.success === false) {
          setIsUploading(false);
          setUploadErrorMessage(parsedResponse.message || "Processing failed. Please try again.");
          return; // Don't close modal, keep it open for user to see error
        }

        // Success case
        setIsUploading(false);
        setUploadComplete(true);
        setUploadErrorMessage(null); // Clear any previous error

        // Show success message in a modal popup
        Modal.success({
          title: "Model Score Processing Started",
          content: parsedResponse.message || "Model score processing started successfully. Please wait 10-15 minutes for completion.",
          onOk: () => {
            // Refetch table to show new audience
            if (refetchHashedEmailLists) {
              refetchHashedEmailLists();
            }
            // Close the guided workflow modal
            handleClose();
          }
        });

      } else {
        setIsUploading(false);
        message.error("Error processing model score. Please try again.");
      }

    } catch (error) {
      setIsUploading(false);
      message.error("Error processing model score: " + error.message);
    }
  };

  // Step 1: Upload Emails
  const handleUploadEmails = async () => {
    let encryptedEmails = [];

    if (isNameEmpty()) {
      return message.error("Please input list name.");
    }

    if (!isNameValid()) {
      return message.error("Name can only contain letters, numbers, dash (-) and underscore (_). No spaces or other special characters allowed.");
    }

    // Special handling for lookalike with existing audience
    if (selectedDataType === "lookalike" && selectedLookalikeSource === "existing") {
      // Call the new function that handles confirmation and upload
      return handleUploadToModelScore();
    }

    // Validation based on data type
    // For lookalike CSV upload path, validate based on selectedCsvDataType
    const effectiveDataType =
      selectedDataType === "lookalike" && selectedLookalikeSource === "upload"
        ? selectedCsvDataType
        : selectedDataType;

    if (effectiveDataType === "emails") {
      if (emailsAlreadyHashed) {
        // Validate that data matches the selected hash format
        const hashValidation = validateHashFormat(emails, selectedHashingMethod);
        if (!hashValidation.isValid) {
          const expectedLength = getHashLength(selectedHashingMethod);
          const formatMsg = hashValidation.detectedFormat
            ? `Your data appears to be ${hashValidation.detectedFormat}.`
            : `Your data is not in a valid ${selectedHashingMethod} format.`;
          return message.warning(
            `${formatMsg} Expected ${selectedHashingMethod} hashed data (${expectedLength} hexadecimal characters). Please select the correct hashing method or re-hash your data.`
          );
        }

        if (containsEmail(emails)) {
          return message.warning(
            "One or more of the values in your data contains an email address. Please ensure that all values are hashed or use an email-free dataset."
          );
        }
      }
    } else if (effectiveDataType === "phones") {
      if (emailsAlreadyHashed) {
        // Validate that data matches the selected hash format
        const hashValidation = validateHashFormat(emails, selectedHashingMethod);
        if (!hashValidation.isValid) {
          const expectedLength = getHashLength(selectedHashingMethod);
          const formatMsg = hashValidation.detectedFormat
            ? `Your data appears to be ${hashValidation.detectedFormat}.`
            : `Your data is not in a valid ${selectedHashingMethod} format.`;
          return message.warning(
            `${formatMsg} Expected ${selectedHashingMethod} hashed data (${expectedLength} hexadecimal characters). Please select the correct hashing method or re-hash your data.`
          );
        }

        if (containsPhoneNumber(emails)) {
          return message.warning(
            "One or more of the values in your data contains a phone number. Please ensure that all values are hashed or use a phone number-free dataset."
          );
        }
      }
    }

    // Confirm exclusion of invalid records before uploading
    if (invalidRecords.length > 0) {
      const proceed = await new Promise((resolve) => {
        Modal.confirm({
          title: "Invalid Records Detected",
          icon: <ExclamationCircleOutlined />,
          content: `${invalidRecords.length.toLocaleString()} invalid records will be excluded from the upload. Continue with ${emails.length.toLocaleString()} valid records?`,
          okText: "Continue Upload",
          cancelText: "Cancel",
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!proceed) return;
    }

    // Initialize upload progress state (after validation passes)
    startUpload({
      audienceName: form.getFieldValue("name"),
      dataType: selectedDataType,
      totalRecords: emails.length,
      savedRecords: 0,
      currentStep: 0,
      emailListId: null,
    });

    // Set uploading state immediately to show loading indicator
    setIsUploading(true);

    // Defer hashing operation to next tick to allow UI to update first
    await new Promise(resolve => setTimeout(resolve, 0));

    if (!emailsAlreadyHashed) {
      // For large datasets, process hashing in chunks to prevent UI blocking
      if (emails.length > 5000) {
        encryptedEmails = [];
        const hashChunkSize = 5000;
        for (let i = 0; i < emails.length; i += hashChunkSize) {
          const chunk = emails.slice(i, i + hashChunkSize);
          const hashedChunk = chunk.map(email =>
            encryptStringBasedOnHashSelection(email)
          );
          encryptedEmails.push(...hashedChunk);

          // Yield to browser between chunks to keep UI responsive
          if (i + hashChunkSize < emails.length) {
            await new Promise(resolve => setTimeout(resolve, 0));
          }
        }
      } else {
        // For smaller datasets, hash all at once
        encryptedEmails = emails.map(email =>
          encryptStringBasedOnHashSelection(email)
        );
      }
      setEmails(encryptedEmails);
    } else {
      encryptedEmails = emails;
    }

    const chunkSize = 3000;

    let progressCount = 0;
    let isLastIteration = false;

    try {
      const response = await createHashedEmailList({
        name: form.getFieldValue("name"),
        stepProgress: 1,
        status: "UPLOADED",
        typeOfFirstPartyData: selectedDataType // Always use selectedDataType (lookalike, emails, phones)
      });

      const emailListId = response.data.createHashedEmailList.id;
      setCreatedEmailListId(emailListId);

      updateUploadProgress({
        emailListId: emailListId,
      });

      for (let i = 0; i < encryptedEmails.length; i += chunkSize) {
        const chunk = encryptedEmails.slice(i, i + chunkSize);
        progressCount += chunkSize;

        isLastIteration = i + chunkSize >= encryptedEmails.length;
        const response = await bulkCreateHashedEmailAddress({
          emailListId: emailListId,
          emails: chunk,
          refetch: isLastIteration,
        });

        const uniqueEmailsCount = response.data.bulkCreateHashedEmailAddress.uniqueEmails;

        setHashedEmailCount(
          prevCount => prevCount + uniqueEmailsCount
        );

        const currentProgress = Math.floor((progressCount / encryptedEmails.length) * 100);
        setProgress(currentProgress);

        updateUploadProgress({
          progress: currentProgress,
          savedRecords: progressCount > encryptedEmails.length ? encryptedEmails.length : progressCount,
        });
      }

      await updateHashedEmailList({
        id: emailListId,
        tag: String(encryptedEmails.length)
      });

      setIsUploading(false);
      setUploadComplete(true);

      completeUpload({
        savedRecords: savedHashedEmailCount + encryptedEmails.length,
        currentStep: 0,
      });

      message.success("Email upload completed successfully!");

    } catch (error) {
      setIsUploading(false);

      failUpload(error.message || "Error uploading emails");

      message.error("Error uploading emails: " + error.message);
    }
  };

  // Step 2: Map to Device IDs
  const handleMapToDeviceIds = async () => {
    if (!createdEmailListId) {
      return message.error("No email list found to process");
    }

    try {
      setIsMappingDevices(true);
      
      const response = await activateHashedEmailList({
        orgID: currentOrg?.id,
        hashedEmailListIds: [createdEmailListId],
        dsp: selectedDspProvider.toUpperCase()
      });
      
      if (
        response &&
        response.data &&
        response.data.activateHashedEmailList &&
        response.data.activateHashedEmailList.success
      ) {
        setIsMappingDevices(false);
        setIsMappingComplete(true);
        
        message.success("Device IDs mapping completed successfully!");
      } else if (
        response &&
        response.data &&
        response.data.activateHashedEmailList &&
        response.data.activateHashedEmailList.error
      ) {
        setIsMappingDevices(false);
        message.error(response.data.activateHashedEmailList.error);
      } else {
        setIsMappingDevices(false);
        message.error(
          "No device IDs were processed. Please make sure there are emails to process."
        );
      }
    } catch (error) {
      setIsMappingDevices(false);
      message.error("Error mapping device IDs: " + error.message);
    }
  };


  const handleNext = () => {
    if (currentStep === 0 && !isUploadComplete) {
      handleUploadEmails();
    } else if (currentStep === 1 && !isMappingComplete) {
      handleMapToDeviceIds();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleFinishAndCheckLater = () => {
    message.success("Workflow completed! You can check the status later from the main view.");
    handleClose();
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleClose = async () => {
    // First call onClose to close the modal immediately
    onClose();

    // Then reset all states after the modal is closed
    setTimeout(() => {
      // Don't reset state if upload is in progress or minimized
      if (isMinimized || (uploadState && uploadState.status === "uploading")) {
        return;
      }

      setCurrentStep(0);
      form.resetFields();
      setEmails([]);
      setProgress(0);
      setHashedEmailCount(0);
      setUploadComplete(false);
      setIsMappingComplete(false);
      setEmailsAlreadyHashed(false);
      setHemAutoDetected(false);
      setSelectedHashingMethod("SHA256");
      setInvalidRecords([]);
      setCreatedEmailListId(null);
      setSelectedLookalikeSource("existing");
      setSelectedSourceAudience(null);
      setShowSourceAudienceModal(false);
      setSelectedCsvDataType("emails");
      // Don't reset hasActivatedAudiences - let the useEffect handle it based on actual data
      setUploadErrorMessage(null); // Clear upload error message

      // Reset per-data-type name storage
      setEmailsAudienceName("");
      setPhonesAudienceName("");
      setLookalikeAudienceName("");

      // Clear upload progress context if not minimized
      if (!isMinimized) {
        clearUpload();
      }

      // Refetch the table to show any newly created audiences (only if an audience was actually created)
      if (refetchHashedEmailLists && createdEmailListId) {
        try {
          refetchHashedEmailLists();
        } catch (error) {
          console.error("Error refetching email lists:", error);
        }
      }
    }, 0);
  };

  const getEmailModalColumns = () => [
    {
      title: getDataTypeLabel(),
      dataIndex: "email",
      key: "email",
      render: text => {
        // Check if it's a valid hash first (regardless of toggle state)
        const isValidHash = isValidHashedValue(text);

        // Check if it's in original format (email or phone)
        // For lookalike, we always expect hashes
        const isOriginalFormat = selectedDataType === "emails"
          ? emailRegex.test(text)
          : selectedDataType === "phones"
          ? phoneRegex.test(text)
          : false; // lookalike should never be in original format

        // Validation logic
        let isValid;
        if (selectedDataType === "lookalike") {
          // Lookalike must always be valid hashes
          isValid = isValidHash;
        } else {
          // For emails/phones: if toggle is ON, expect hashes; if OFF, expect original format
          isValid = emailsAlreadyHashed
            ? isValidHash  // When toggle is ON, prefer hashes but accept both
            : (isOriginalFormat || isValidHash); // When toggle is OFF, prefer original but accept hashes too
        }

        return (
          <span
            style={{
              color: isValid ? "inherit" : "red",
            }}
          >
            {text} {isValid ? "" : "(Invalid Value)"}
          </span>
        );
      },
    },
    {
      title: "Option",
      dataIndex: "option",
      key: "option",
      render: (_, email) => (
        <Popconfirm
          title={`Are you sure you want to delete this ${getDataTypeLabel().toLowerCase()}?`}
          onConfirm={async e => {
            const index = emails.findIndex(element => element === email.email);
            if (index !== -1) {
              const newData = [...emails];
              newData.splice(index, 1);
              setEmails(newData);
              message.success(`${getDataTypeLabel()} deleted`);
            }
          }}
          okText="Yes"
          cancelText="No"
          disabled={isUploadComplete}
        >
          <DeleteOutlined style={{ color: isUploadComplete ? '#d9d9d9' : 'inherit', cursor: isUploadComplete ? 'not-allowed' : 'pointer' }} />
        </Popconfirm>
      ),
    },
  ];

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div>
            <Title level={4}>Upload Your {getDataTypeLabel()} List</Title>
            <Text style={{ display: "block", marginBottom: "24px" }}>
              Upload a CSV file containing your data. The system will process and hash them for privacy.
            </Text>

            {/* Data Type Selection */}
            <Row style={{ marginBottom: "24px" }}>
              <Col span={24}>
                <Text strong style={{ display: "block", marginBottom: "8px" }}>Select Data Type:</Text>
                <Radio.Group
                  value={selectedDataType}
                  onChange={(e) => setSelectedDataType(e.target.value)}
                  disabled={isUploading || isUploadComplete}
                >
                  <Radio value="emails">Email Addresses</Radio>
                  <Radio value="phones">Phone Numbers</Radio>
                  <Radio value="lookalike">Lookalike Audience</Radio>
                </Radio.Group>
              </Col>
            </Row>

            {/* Lookalike Source Selection */}
            {selectedDataType === "lookalike" && (
              <Row style={{ marginBottom: "24px" }}>
                <Col span={24}>
                  <Card
                    size="small"
                    style={{
                      backgroundColor: "#f0f5ff",
                      border: "1px solid #adc6ff"
                    }}
                  >
                    <Text strong style={{ display: "block", marginBottom: "12px" }}>
                      Choose Lookalike Audience Source:
                    </Text>

                    {/* Loading indicator while checking for audiences */}
                    {loadingAudienceCheck && selectedLookalikeSource === "existing" && (
                      <div style={{ marginBottom: "12px", textAlign: "center" }}>
                        <Spin size="small" />
                        <Text type="secondary" style={{ marginLeft: "8px", fontSize: "12px" }}>
                          Checking for activated audiences...
                        </Text>
                      </div>
                    )}

                    {/* Informational banner about creating new audiences */}
                    <Alert
                      message="Audience you want as a source doesn't exist yet?"
                      description={
                        <div>
                          <Text style={{ fontSize: "12px" }}>
                            If you want to create a lookalike audience from a new audience that doesn't exist yet, you'll need to:
                          </Text>
                          <ol style={{ fontSize: "12px", marginTop: "8px", marginBottom: "0", paddingLeft: "20px" }}>
                            <li>First, create your audience by uploading hashed email addresses</li>
                            <li>Wait for the audience to complete activation (you'll see its status change to "Activated")</li>
                            <li>Once activated, you can return here and select it as a source for your lookalike audience</li>
                          </ol>
                        </div>
                      }
                      type="info"
                      showIcon
                      style={{ marginBottom: "16px" }}
                      aria-label="New audience creation information"
                    />

                    <div style={{ padding: "12px 0" }}>
                      <div>
                        <Text strong>Use Existing Activated Audience</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          Select an already activated audience as the source for your lookalike audience
                        </Text>
                      </div>
                    </div>

                    {/* Select Source Audience Button */}
                    {selectedLookalikeSource === "existing" && (
                      <div style={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #d9d9d9" }}>
                        <Tooltip title={!hasActivatedAudiences && !loadingAudienceCheck ? "No activated audiences available" : loadingAudienceCheck ? "Loading..." : ""}>
                          <Button
                            type="default"
                            icon={<UsergroupAddOutlined />}
                            onClick={() => setShowSourceAudienceModal(true)}
                            disabled={isUploading || isUploadComplete || loadingAudienceCheck || (!hasActivatedAudiences && !loadingAudienceCheck)}
                            loading={loadingAudienceCheck}
                            style={{ marginBottom: selectedSourceAudience ? "12px" : "0" }}
                          >
                            {selectedSourceAudience ? "Change Source Audience" : "Select Source Audience"}
                          </Button>
                        </Tooltip>
                        {selectedSourceAudience && (
                          <div style={{ marginTop: "8px" }}>
                            <Badge status="success" />
                            <Text style={{ fontSize: "13px", color: "#52c41a" }}>
                              Selected: <Text strong>{selectedSourceAudience.name}</Text>
                            </Text>
                          </div>
                        )}
                        {!selectedSourceAudience && (
                          <div style={{ marginTop: "8px" }}>
                            <Text type="danger" style={{ fontSize: "12px" }}>
                              * Please select a source audience to continue
                            </Text>
                          </div>
                        )}

                        {/* Model Score Pipeline Info Note */}
                        <Alert
                          message="Model Score Processing"
                          description="Selecting an existing activated audience will process it through the Model Score pipeline for lookalike expansion."
                          type="info"
                          showIcon
                          style={{ marginTop: "16px" }}
                          aria-label="Model Score processing information"
                        />
                      </div>
                    )}
                  </Card>
                </Col>
              </Row>
            )}

            {/* Data Type Selection for CSV Upload Path (Lookalike) */}
            {selectedDataType === "lookalike" && selectedLookalikeSource === "upload" && (
              <Row style={{ marginBottom: "24px" }}>
                <Col span={24}>
                  <Card
                    size="small"
                    style={{
                      backgroundColor: "#fff7e6",
                      border: "1px solid #ffd591"
                    }}
                  >
                    <Text strong style={{ display: "block", marginBottom: "12px" }}>
                      Select Data Type for Lookalike Source:
                    </Text>
                    <Text type="secondary" style={{ display: "block", marginBottom: "12px", fontSize: "13px" }}>
                      Choose whether your CSV contains email addresses or phone numbers. This data will be used as the source for creating a lookalike audience.
                    </Text>
                    <Radio.Group
                      value={selectedCsvDataType}
                      onChange={(e) => setSelectedCsvDataType(e.target.value)}
                      disabled={isUploading || isUploadComplete}
                    >
                      <Radio value="emails" aria-label="Email addresses data type">
                        Email Addresses
                      </Radio>
                      <Radio value="phones" aria-label="Phone numbers data type">
                        Phone Numbers
                      </Radio>
                    </Radio.Group>
                  </Card>
                </Col>
              </Row>
            )}

            <Row gutter={16} style={{ marginBottom: "24px" }}>
              <Col span={16}>
                <Form form={form} layout="vertical">
                  <Form.Item
                    label="Audience Name"
                    name="name"
                    rules={[
                      { required: true, message: "Please input your audience name." },
                      {
                        pattern: /^[a-zA-Z0-9_-]+$/,
                        message: "Name can only contain letters, numbers, dash (-) and underscore (_).",
                      },
                      {
                        max: 30,
                        message: "Audience name cannot exceed 30 characters.",
                      },
                    ]}
                  >
                    <Input
                      placeholder="Enter audience name"
                      disabled={isUploading || isUploadComplete}
                      maxLength={30}
                      showCount
                    />
                  </Form.Item>
                </Form>
              </Col>
              {/* Only show CSV template when NOT using existing audience for lookalike */}
              {!(selectedDataType === "lookalike" && selectedLookalikeSource === "existing") && (
                <Col span={8}>
                  <Form.Item label=" ">
                    <TemplateCSVDownloader
                      tooltipMessage={`Download CSV template for ${
                        selectedDataType === "lookalike" && selectedLookalikeSource === "upload"
                          ? (selectedCsvDataType === "emails" ? "Emails" : "Phone Numbers")
                          : getDataTypeLabel()
                      } list`}
                      rows={
                        selectedDataType === "lookalike" && selectedLookalikeSource === "upload"
                          ? (selectedCsvDataType === "emails"
                              ? HashedEmailConfig.csvTemplate.rows
                              : PhoneNumberConfig.csvTemplate.rows)
                          : (selectedDataType === "emails"
                              ? HashedEmailConfig.csvTemplate.rows
                              : selectedDataType === "phones"
                              ? PhoneNumberConfig.csvTemplate.rows
                              : LookalikeConfig.csvTemplate.rows)
                      }
                      templateName={
                        selectedDataType === "lookalike" && selectedLookalikeSource === "upload"
                          ? (selectedCsvDataType === "emails"
                              ? HashedEmailConfig.csvTemplate.templateName
                              : PhoneNumberConfig.csvTemplate.templateName)
                          : (selectedDataType === "emails"
                              ? HashedEmailConfig.csvTemplate.templateName
                              : selectedDataType === "phones"
                              ? PhoneNumberConfig.csvTemplate.templateName
                              : LookalikeConfig.csvTemplate.templateName)
                      }
                      title={`Sample ${
                        selectedDataType === "lookalike" && selectedLookalikeSource === "upload"
                          ? (selectedCsvDataType === "emails" ? "Emails" : "Phone Numbers")
                          : getDataTypeLabel()
                      } list template`}
                      styles={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>

            {!(selectedDataType === "lookalike" && selectedLookalikeSource === "existing") && (
              <Popover
                content={
                  <ol style={{ fontSize: "14px" }}>
                    <li>Select your data type (Email Addresses, Phone Numbers, or Lookalike Audience)</li>
                    <li>Enter the Audience List Name</li>
                    <li>Upload a CSV file. Remove headers from columns. Download the Sample Template for reference</li>
                    <li>Select your desired options:
                      <ol>
                        <li>If your CSV contains hashed data, the system will auto-detect and enable pre-hashed mode</li>
                        <li>Alternatively, select your desired hashing method</li>
                      </ol>
                    </li>
                    <li>Click on the Next button to upload</li>
                    <li>Select your DSP Provider destination in Step 2</li>
                  </ol>
                }
                title="Instructions"
              >
                <Button type="default" size="small" style={{ marginBottom: "16px" }}>
                  Show Instructions
                </Button>
              </Popover>
            )}


            {/* Only show Data Hashed toggle when NOT using existing audience for lookalike */}
            {!(selectedDataType === "lookalike" && selectedLookalikeSource === "existing") && (
              <Row gutter={16} style={{ marginBottom: "16px" }}>
                <Col span={12}>
                  <Text>Data is already hashed </Text>
                  <Tooltip title={
                    emails.length === 0
                      ? "Upload data first to enable this option"
                      : hemAutoDetected
                        ? `Auto-detected ${selectedHashingMethod} format`
                        : "Enable this option if the data is already hashed"
                  }>
                    <Switch
                      checked={emailsAlreadyHashed}
                      onChange={(checked) => {
                        setEmailsAlreadyHashed(checked);
                        if (!checked && hemAutoDetected) {
                          setHemAutoDetected(false);
                        }
                      }}
                      disabled={emails.length === 0 || isUploadComplete || hemAutoDetected}
                    />
                  </Tooltip>
                  {emailsAlreadyHashed && emails.length > 0 && (
                    <div style={{ marginTop: "4px" }}>
                      <Badge status="success" text={
                        <Text style={{ fontSize: "12px", color: "#52c41a" }}>
                          {hemAutoDetected ? `Auto-detected ${selectedHashingMethod} format` : "Auto-detected hashed format"}
                        </Text>
                      } />
                    </div>
                  )}
                </Col>
                <Col span={12}>
                  <Text>Hashed Method Selection </Text>
                  <Tooltip title={hemAutoDetected ? `Auto-detected as ${selectedHashingMethod}` : "Select the hashing method used for your data"}>
                    <Select
                      value={selectedHashingMethod}
                      disabled={emailsAlreadyHashed || hemAutoDetected || isUploadComplete}
                      onChange={(value) => setSelectedHashingMethod(value)}
                      style={{ width: "100%" }}
                      options={[
                        { value: "MD5", label: "MD5 (32 characters)" },
                        { value: "SHA1", label: "SHA-1 (40 characters)" },
                        { value: "SHA256", label: "SHA-256 (64 characters)" },
                        { value: "SHA512", label: "SHA-512 (128 characters)" },
                      ]}
                    />
                  </Tooltip>
                </Col>
              </Row>
            )}

            {(isUploading || isUploadComplete) && (
              <div style={{ marginBottom: "20px", textAlign: "center" }}>
                <Progress
                  percent={progress}
                  status={isUploading ? "active" : "success"}
                />
                {isUploadComplete && (
                  <div>
                    <Text
                      type="success"
                      style={{ color: "#52c41a", textAlign: "center", display: "block", marginTop: "8px" }}
                    >
                      Upload Complete. {`${savedHashedEmailCount}/${emails.length} ${getDataTypeLabel().toLowerCase()} were successfully saved.`}
                    </Text>
                    <Text
                      type="secondary"
                      style={{ fontSize: "12px", textAlign: "center", display: "block", marginTop: "4px" }}
                    >
                      To activate this audience, click "Next" to proceed to Step 2 and click "Map to Device IDs".
                    </Text>
                  </div>
                )}
              </div>
            )}

            {/* Only show CSV upload if not using existing audience for lookalike */}
            {!(selectedDataType === "lookalike" && selectedLookalikeSource === "existing") && (
              <Spin spinning={isUploading || isParsing} tip={isParsing ? "Parsing CSV file..." : "Uploading..."}>
                {emails.length === 0 && invalidRecords.length === 0 ? (
                  <Dragger
                    accept={".csv"}
                    showUploadList={false}
                    multiple={false}
                    customRequest={customRequest}
                    disabled={isParsing}
                    style={{ marginTop: "20px" }}
                  >
                    <p className="ant-upload-drag-icon">
                      <UploadOutlined />
                    </p>
                    <p className="ant-upload-text">
                      Click or drag CSV file to this area to upload
                    </p>
                    <p className="ant-upload-hint">
                      {isParsing ? "Processing your file..." : `Import your ${getDataTypeLabel()} here.`}
                    </p>
                  </Dragger>
                ) : (
                  <div>
                    <Row justify="space-between" align="middle" style={{ marginBottom: "10px" }}>
                      <Col>
                        <Space>
                          {emails.length > 0 && emails.length <= 1000 && (
                            <Upload
                              accept={".csv"}
                              showUploadList={false}
                              multiple={false}
                              customRequest={customRequest}
                            >
                              <Button
                                icon={<UploadOutlined />}
                                disabled={isUploadComplete || isParsing}
                                loading={isParsing}
                              >
                                {isParsing ? "Parsing..." : "Add more"}
                              </Button>
                            </Upload>
                          )}
                          <Popconfirm
                            title={isUploading ? "Cancel upload and clear all records?" : "Clear all records?"}
                            description={isUploading ? "This will stop the current upload and remove all loaded records." : "This will remove all loaded records."}
                            onConfirm={async () => {
                              // Clear all records
                              setEmails([]);
                              setInvalidRecords([]);

                              // Reset hash-related states
                              setEmailsAlreadyHashed(false);
                              setHemAutoDetected(false);
                              setSelectedHashingMethod("SHA256");
                              setDetectedInvalidFormat(null);

                              // If upload is in progress, cancel it
                              if (isUploading) {
                                setIsUploading(false);
                                setProgress(0);
                                setHashedEmailCount(0);
                                setUploadComplete(false);

                                // Clear upload progress context
                                clearUpload();

                                // Refetch the table to remove "Uploading" status
                                if (refetchHashedEmailLists) {
                                  await refetchHashedEmailLists();
                                }

                                message.info("Upload cancelled and records cleared");
                              } else {
                                message.success("Records cleared successfully");
                              }
                            }}
                            okText="Yes, Clear"
                            cancelText="No"
                            okType="danger"
                            disabled={(emails.length === 0 && invalidRecords.length === 0) || isParsing}
                          >
                            <Button
                              icon={<DeleteOutlined />}
                              disabled={(emails.length === 0 && invalidRecords.length === 0) || isParsing}
                              danger={isUploading}
                            >
                              {isUploading ? "Cancel & Clear" : "Clear all"}
                            </Button>
                          </Popconfirm>
                          {emails.length > 0 && emails.length <= 1000 && (
                            <Button
                              icon={sortIcon}
                              onClick={handleSortByName}
                              disabled={emails.length === 0 || isParsing}
                            >
                              Sort
                            </Button>
                          )}
                        </Space>
                      </Col>
                      <Col>
                        <Text type="secondary">
                          {invalidRecords.length > 0
                            ? `Valid: ${emails.length.toLocaleString()} | Invalid: ${invalidRecords.length.toLocaleString()}`
                            : `${getDataTypeLabel()} Count: ${emails.length.toLocaleString()}`
                          }
                        </Text>
                      </Col>
                    </Row>
                    {emails.length > 1000 ? (
                      <Alert
                        message={
                          invalidRecords.length > 0
                            ? `${emails.length.toLocaleString()} valid records loaded (${invalidRecords.length.toLocaleString()} invalid excluded)`
                            : `${emails.length.toLocaleString()} records loaded successfully`
                        }
                        description={
                          invalidRecords.length > 0
                            ? `Large dataset detected. Table preview is hidden to optimize performance. ${emails.length.toLocaleString()} valid records will be uploaded. ${invalidRecords.length.toLocaleString()} invalid records are shown below.`
                            : `Large dataset detected. Table preview is hidden to optimize performance. All ${emails.length.toLocaleString()} records will be uploaded when you click the Upload button.`
                        }
                        type={invalidRecords.length > 0 ? "warning" : "success"}
                        showIcon
                        style={{ marginTop: "12px" }}
                      />
                    ) : emails.length > 0 ? (
                      <PaginationTable
                        columns={getEmailModalColumns()}
                        data={emails.map(email => ({ email }))}
                        total={emails.length}
                        bordered={false}
                        size="small"
                        pageSize={10}
                        style={{ height: "300px", overflowY: "auto" }}
                      />
                    ) : null}

                    {/* Invalid Records Section */}
                    {invalidRecords.length > 0 && (
                      <div style={{ marginTop: "16px" }}>
                        <Alert
                          type="warning"
                          showIcon
                          message={
                            emails.length === 0
                              ? `All ${invalidRecords.length.toLocaleString()} records are invalid — no valid records to upload`
                              : `${invalidRecords.length.toLocaleString()} invalid records found`
                          }
                          description={
                            detectedInvalidFormat
                              ? `Data appears to be ${detectedInvalidFormat} which is not supported. Only plain emails/phones or hashed data (MD5, SHA-1, SHA-256, SHA-512) are accepted.${invalidRecords.length > 5000 ? " Download the CSV to review them." : ""}`
                              : invalidRecords.length > 5000
                                ? "Too many invalid records to display inline. Download the CSV to review them."
                                : "The following records are invalid and will be excluded from the upload."
                          }
                          style={{ marginBottom: "12px" }}
                          action={
                            <Button
                              size="small"
                              icon={<DownloadOutlined />}
                              onClick={downloadInvalidRecordsCsv}
                            >
                              Download CSV
                            </Button>
                          }
                        />
                        {invalidRecords.length <= 5000 && (
                          <Table
                            dataSource={invalidRecords.map((r, i) => ({ key: i, row: r.row, value: r.value }))}
                            columns={[
                              { title: "Row #", dataIndex: "row", key: "row", width: 80 },
                              { title: "Value", dataIndex: "value", key: "value", ellipsis: true },
                            ]}
                            size="small"
                            pagination={{ pageSize: 10, size: "small" }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}
              </Spin>
            )}
          </div>
        );

      case 1:
        return (
          <div>
            <Title level={4}>Map to Device IDs</Title>
            <Text style={{ display: "block", marginBottom: "16px" }}>
              Converting {getDataTypeLabel().toLowerCase()} into device identifiers through secure hashing and identity matching.
              This process usually takes about 30 minutes to complete.
            </Text>

            {/* Audience Info Display */}
            {(form.getFieldValue("name") || emails.length > 0) && (
              <Card
                size="small"
                style={{
                  marginBottom: "20px",
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #e9ecef"
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <Text strong style={{ color: "#495057" }}>Audience: </Text>
                    <Text style={{ color: "#6c757d" }}>
                      {form.getFieldValue("name") || "Untitled Audience"}
                    </Text>
                  </div>
                  <div>
                    <Text strong style={{ color: "#495057" }}>{getDataTypeLabel()} Count: </Text>
                    <Text style={{ color: "#6c757d" }}>{emails.length.toLocaleString()}</Text>
                  </div>
                </div>
              </Card>
            )}


            {/* DSP Selection */}
            <Form layout="vertical" style={{ marginBottom: "20px" }}>
              <Form.Item label="Select DSP Provider">
                <Text style={{ display: "block", marginBottom: "8px", fontSize: "14px", color: "#666" }}>
                  Which DSP platform would you like to activate this audience for?
                </Text>
                <Radio.Group
                  value={selectedDspProvider}
                  onChange={(e) => setSelectedDspProvider(e.target.value)}
                  disabled={isMappingDevices || isMappingComplete}
                >
                  <Radio value="liquidm" disabled={selectedDataType === "phones" || selectedDataType === "lookalike" || isMappingComplete}>
                    {getDSPDisplayName('LiquidM', providersVisible)} {(selectedDataType === "phones" || selectedDataType === "lookalike") && <span style={{ color: "#8c8c8c" }}>(Not available for {selectedDataType === "phones" ? "phone numbers" : "lookalike audiences"})</span>}
                  </Radio>
                  <Radio value="nexxen" disabled={isMappingComplete}>
                    {getDSPDisplayName('Nexxen', providersVisible)}
                  </Radio>
                </Radio.Group>
              </Form.Item>
            </Form>

            <Card style={{ textAlign: "center", marginTop: "20px" }}>
              {isMappingComplete ? (
                <div>
                  <CheckCircleOutlined style={{ fontSize: "48px", color: "#52c41a", marginBottom: "16px" }} />
                  <Title level={4} style={{ color: "#52c41a" }}>Device IDs Mapping Complete!</Title>
                  <Text>Your {getDataTypeLabel().toLowerCase()} has been successfully converted to device identifiers. This process usually takes about 30 minutes to complete.</Text>
                </div>
              ) : (
                <div>
                  <SettingOutlined style={{ fontSize: "48px", color: "#1890ff", marginBottom: "16px" }} />
                  <Title level={4}>Ready to Map to Device IDs</Title>
                  <Text style={{ display: "block", marginBottom: "16px" }}>
                    Click "Map to Device IDs" to begin converting your {getDataTypeLabel().toLowerCase()} into device identifiers.
                  </Text>
                  {isMappingDevices && (
                    <div style={{ marginTop: "20px" }}>
                      <Spin size="large" />
                      <br />
                      <Text style={{ marginTop: "16px", display: "block" }}>Processing device IDs...</Text>
                    </div>
                  )}
                </div>
              )}
            </Card>
          </div>
        );


      default:
        return null;
    }
  };

  const getNextButtonText = () => {
    if (currentStep === 0) {
      // For lookalike with existing audience source
      if (selectedDataType === "lookalike" && selectedLookalikeSource === "existing") {
        return "Upload";
      }
      return isUploadComplete ? "Next" : "Upload";
    } else if (currentStep === 1) {
      return isMappingComplete ? "Finish and Check Later" : "Map to Device IDs";
    }
    return "Next";
  };

  const getNextButtonIcon = () => {
    if (currentStep === 0) {
      return isUploadComplete ? <ArrowRightOutlined /> : <UploadOutlined />;
    } else if (currentStep === 1) {
      return isMappingComplete ? <SaveOutlined /> : <UsergroupAddOutlined />;
    }
    return <ArrowRightOutlined />;
  };

  const isNextDisabled = () => {
    if (currentStep === 0) {
      // For lookalike with existing audience path
      if (selectedDataType === "lookalike" && selectedLookalikeSource === "existing") {
        return !selectedSourceAudience || isNameEmpty() || isUploading || isParsing;
      }
      // For regular upload path
      return emails.length === 0 || isUploading || isParsing;
    } else if (currentStep === 1) {
      return !isUploadComplete || isMappingDevices || !selectedDspProvider;
    }
    return false;
  };

  const isComplete = false; // Never show the complete state since workflow ends after step 1

  return (
    <Modal
      title="Add New Audience"
      open={visible}
      onCancel={handleClose}
      closable={!isUploading && !isParsing}
      maskClosable={!isUploading && !isParsing}
      keyboard={!isUploading && !isParsing}
      width="90%"
      style={{ top: 20 }}
      bodyStyle={{
        maxHeight: "80vh",
        overflowY: "hidden",
        padding: "0",
        position: "relative"
      }}
        footer={
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div>
            {isUploading && currentStep === 0 && (
              <Tooltip title="Minimize and continue upload in background">
                <Button
                  key="minimize"
                  onClick={() => {
                    minimizeUpload();
                    onClose();
                  }}
                  icon={<MinusOutlined />}
                  size="large"
                >
                  Minimize
                </Button>
              </Tooltip>
            )}
          </div>
          <Space size="large">
            {!isMappingComplete && (
              <Button
                key="close"
                onClick={handleClose}
                icon={<CloseOutlined />}
                disabled={isUploading || isParsing}
                size="large"
              >
                Close
              </Button>
            )}

            {!isComplete && currentStep === 0 && (
              <>
                <Tooltip
                  title={uploadErrorMessage}
                  open={!!uploadErrorMessage}
                  color="red"
                  overlayStyle={{ maxWidth: '400px' }}
                  trigger={['hover', 'focus']}
                  onOpenChange={(visible) => {
                    // Allow tooltip to be dismissed by moving mouse away or losing focus
                    if (!visible) {
                      // Optionally clear error on dismiss - uncomment if needed
                      // setUploadErrorMessage(null);
                    }
                  }}
                >
                  <Button
                    key="next"
                    type="primary"
                    onClick={handleNext}
                    disabled={isNextDisabled()}
                    loading={isUploading}
                    icon={getNextButtonIcon()}
                    aria-label={getNextButtonText()}
                    aria-describedby={uploadErrorMessage ? "upload-error-message" : undefined}
                    aria-invalid={!!uploadErrorMessage}
                    size="large"
                  >
                    {getNextButtonText()}
                  </Button>
                </Tooltip>
                {uploadErrorMessage && (
                  <span
                    id="upload-error-message"
                    role="alert"
                    aria-live="polite"
                    style={{
                      position: 'absolute',
                      width: '1px',
                      height: '1px',
                      padding: 0,
                      margin: '-1px',
                      overflow: 'hidden',
                      clip: 'rect(0, 0, 0, 0)',
                      whiteSpace: 'nowrap',
                      border: 0
                    }}
                  >
                    {uploadErrorMessage}
                  </span>
                )}
              </>
            )}

            {!isComplete && currentStep === 1 && !isMappingComplete && (
              <Button
                key="map"
                type="primary"
                onClick={handleNext}
                disabled={isNextDisabled()}
                loading={isMappingDevices}
                icon={getNextButtonIcon()}
                size="large"
              >
                {getNextButtonText()}
              </Button>
            )}

            {!isComplete && currentStep === 1 && isMappingComplete && (
              <Button
                key="finish"
                type="primary"
                onClick={handleFinishAndCheckLater}
                icon={getNextButtonIcon()}
                size="large"
              >
                {getNextButtonText()}
              </Button>
            )}
          </Space>
        </div>
      }
    >
      <Row style={{ minHeight: "80vh" }}>
        {/* Left Sidebar - Steps Navigation */}
        <Col span={6} style={{ 
          backgroundColor: "#f5f5f5", 
          padding: "24px 16px",
          borderRight: "1px solid #e8e8e8"
        }}>
          <div style={{ marginBottom: "24px" }}>
            <Title level={4} style={{ margin: "0 0 8px 0", color: "#262626" }}>Progress</Title>
            <Text type="secondary" style={{ fontSize: "12px" }}>Step {currentStep + 1} of {selectedDataType === "lookalike" ? 1 : steps.length}</Text>
          </div>

          <Steps
            current={-1}
            direction="vertical"
            size="small"
          >
            {steps
              .filter((_, index) => {
                // Hide Step 2 (Convert to Device IDs) for lookalike audiences
                if (selectedDataType === "lookalike" && index === 1) {
                  return false;
                }
                return true;
              })
              .map((item, index) => {
                const isCompleted =
                  index < currentStep ||
                  (index === 0 && isUploadComplete) ||
                  (index === 1 && isMappingComplete);

                return (
                  <Step
                    key={item.title}
                    title={item.title}
                    description={item.description}
                    icon={
                      isCompleted ? (
                        <CheckCircleOutlined style={{ color: "#52c41a", fontSize: "20px" }} />
                      ) : (
                        React.cloneElement(item.icon, {
                          style: {
                            color: index === currentStep ? "#1890ff" : "#bfbfbf",
                            fontSize: "20px"
                          }
                        })
                      )
                    }
                  />
                );
              })}
          </Steps>
        </Col>
        
        {/* Right Content Area */}
        <Col span={18} style={{ 
          backgroundColor: "#ffffff",
          display: "flex",
          flexDirection: "column"
        }}>
          <div style={{ 
            flex: 1, 
            overflowY: "auto", 
            maxHeight: "calc(80vh - 80px)",
            padding: "24px"
          }}>
            {renderStepContent()}
          </div>
        </Col>
      </Row>

      {/* Source Audience Selection Modal */}
      <SourceAudienceSelectionModal
        visible={showSourceAudienceModal}
        onClose={() => setShowSourceAudienceModal(false)}
        onSelect={(audience) => setSelectedSourceAudience(audience)}
        currentOrg={currentOrg}
        onActivatedCountChange={(count) => {
          setHasActivatedAudiences(count > 0);
        }}
        providersVisible={providersVisible}
      />

      {/* Custom parsing progress overlay - scoped to modal only */}
      {isParsing && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10,
          }}
        >
          <Card
            style={{
              width: "400px",
              textAlign: "center",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            }}
          >
            <div style={{ marginBottom: "16px" }}>
              <Progress
                type="circle"
                percent={parsingProgress}
                status="active"
                strokeColor={{
                  "0%": "#108ee9",
                  "100%": "#87d068",
                }}
              />
            </div>
            <Title level={4} style={{ marginBottom: "8px" }}>
              Parsing CSV File
            </Title>
            <Text type="secondary">
              Please wait while we process your file...
            </Text>
          </Card>
        </div>
      )}
    </Modal>
  );
};

export default GuidedWorkflowSteps;
